import { useState, type FormEvent } from 'react';
import { recordingsStorageApi, type RecordingsStorageCheck, type RecordingsStorageView } from '../../api/client';
import { Button, Card, Chip, Dialog, Field, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Call recordings storage (1.12.27): a phone system that keeps its call
 * recordings in cloud storage (a B2 bucket) keeps no copy, so Stitch reads them from the bucket with
 * a read-only key of its own. The bucket stays private. The key is sealed when saved and never
 * shown again; leave its fields empty to keep the saved one.
 */
export function RecordingsStorageCard() {
  const data = useData(() => recordingsStorageApi.get(), []);
  const [view, setView] = useState<RecordingsStorageView | null>(null);
  const v = view ?? data.data;
  const [form, setForm] = useState<{ endpoint: string; bucket: string; region: string; accessKeyId: string; secretAccessKey: string; uploadKeyId: string; uploadSecret: string } | null>(null);
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [check, setCheck] = useState<RecordingsStorageCheck | null>(null);
  const [removing, setRemoving] = useState(false);

  if (data.error) {
    return (
      <Card title="Call recordings storage">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  }
  if (!v) {
    return (
      <Card title="Call recordings storage">
        <Loading />
      </Card>
    );
  }
  const f = form ?? { endpoint: v.endpoint, bucket: v.bucket, region: v.region, accessKeyId: '', secretAccessKey: '', uploadKeyId: '', uploadSecret: '' };
  const edit = (k: keyof typeof f) => (e: { target: { value: string } }) => setForm({ ...f, [k]: e.target.value });

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setProblem(null);
    try {
      await fn();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    await run('save', async () => {
      const change: Parameters<typeof recordingsStorageApi.save>[0] = {};
      if (f.endpoint.trim() !== v!.endpoint) change.endpoint = f.endpoint.trim();
      if (f.bucket.trim() !== v!.bucket) change.bucket = f.bucket.trim();
      if (f.region.trim() !== v!.region) change.region = f.region.trim();
      if (f.accessKeyId.trim()) change.accessKeyId = f.accessKeyId.trim();
      if (f.secretAccessKey.trim()) change.secretAccessKey = f.secretAccessKey.trim();
      if (f.uploadKeyId.trim() || f.uploadSecret.trim()) {
        change.uploadKeyId = f.uploadKeyId.trim();
        change.uploadSecret = f.uploadSecret.trim();
      }
      setView(await recordingsStorageApi.save(change));
      setForm(null);
      setCheck(null);
    });
  }

  const changed =
    form !== null && (f.endpoint !== v.endpoint || f.bucket !== v.bucket || f.region !== v.region || Boolean(f.accessKeyId) || Boolean(f.secretAccessKey) || Boolean(f.uploadKeyId) || Boolean(f.uploadSecret));

  return (
    <Card title="Call recordings storage">
      <form className="card-body" onSubmit={(e) => void save(e)}>
        <p className="muted" style={{ marginTop: 0 }}>
          When the phone system keeps call recordings in cloud storage, it keeps no copy of its own, so Stitch reads them from the bucket with a <strong>read-only</strong> key
          of its own. The bucket stays private. Stitch asks the phone system first, and the bucket only for a recording the phone system doesn’t have.
        </p>
        <div style={{ marginBottom: 12 }}>
          {v.configured ? <Chip tone="ok">Set up</Chip> : <Chip>Not set up</Chip>}
          {v.updatedAt && (
            <small className="muted">
              {' '}
              Saved {shortWhen(v.updatedAt)}
              {v.updatedByName ? ` by ${v.updatedByName}` : ''}
            </small>
          )}
        </div>
        {v.problem && <Notice tone="warn">{v.problem}</Notice>}
        {!v.canSeal && (
          <Notice tone="warn" title="The key can’t be saved here yet">
            Set DATA_ENCRYPTION_KEY in EasyPanel and redeploy once. The key is kept sealed with it.
          </Notice>
        )}
        <Field label="Storage address" placeholder="https://s3.us-east-005.backblazeb2.com" value={f.endpoint} onChange={edit('endpoint')} hint="The bucket’s S3 endpoint, as B2 shows it on the bucket." />
        <Field label="Bucket" placeholder="nyc1-vodia" value={f.bucket} onChange={edit('bucket')} />
        <Field
          label="Region"
          placeholder={v.regionFromAddress ?? 'us-east-005'}
          value={f.region}
          onChange={edit('region')}
          hint={v.regionFromAddress ? `Leave empty to use ${v.regionFromAddress}, from the address.` : 'Leave empty to work it out from the address.'}
        />
        <Field
          label="Key ID"
          autoComplete="off"
          spellCheck={false}
          placeholder={v.accessKeyId.set ? 'Saved (leave empty to keep it)' : 'keyID'}
          value={f.accessKeyId}
          onChange={edit('accessKeyId')}
        />
        <Field
          label="Application key"
          type="password"
          autoComplete="new-password"
          placeholder={v.secretAccessKey.set ? 'Saved (leave empty to keep it)' : 'applicationKey'}
          value={f.secretAccessKey}
          onChange={edit('secretAccessKey')}
          hint="A B2 application key with read-only access to this bucket only. Sealed when saved, never shown again."
        />
        <p className="field-label" style={{ margin: '16px 0 4px' }}>
          Meeting recordings {v.uploadKey.set ? <Chip tone="ok">Upload key saved</Chip> : <Chip>No upload key</Chip>}
        </p>
        <p className="muted small" style={{ marginTop: 0 }}>
          Meetings are recorded into this bucket too, under each company’s own folder. LiveKit’s recorder needs a second key that can write files to it (Read and Write
          Files, this bucket only). Stitch keeps the read-only key above for reading.
        </p>
        <Field
          label="Upload key ID"
          autoComplete="off"
          spellCheck={false}
          placeholder={v.uploadKey.set ? 'Saved (leave empty to keep it)' : 'keyID'}
          value={f.uploadKeyId}
          onChange={edit('uploadKeyId')}
        />
        <Field
          label="Upload application key"
          type="password"
          autoComplete="new-password"
          placeholder={v.uploadKey.set ? 'Saved (leave empty to keep it)' : 'applicationKey'}
          value={f.uploadSecret}
          onChange={edit('uploadSecret')}
          hint="Sealed when saved, never shown again. Changing the address or bucket forgets it unless you enter it again."
        />
        {problem && <Notice tone="crit">{problem}</Notice>}
        {check && (
          <Notice tone={!check.ok ? 'crit' : check.publicBucket ? 'warn' : 'ok'} title={!check.ok ? 'Stitch can’t read it' : check.publicBucket ? 'The bucket is public' : 'Stitch can read it'}>
            {check.message}
          </Notice>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button type="submit" variant="primary" disabled={busy !== '' || !changed}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </Button>
          <Button type="button" disabled={busy !== '' || !v.configured} onClick={() => void run('test', async () => setCheck(await recordingsStorageApi.test()))}>
            {busy === 'test' ? 'Testing…' : 'Test'}
          </Button>
          {(v.endpoint || v.bucket || v.accessKeyId.set) && (
            <Button type="button" variant="ghost" disabled={busy !== ''} onClick={() => setRemoving(true)}>
              Remove
            </Button>
          )}
        </div>
      </form>
      <Dialog
        open={removing}
        onClose={() => setRemoving(false)}
        title="Remove the call recordings storage?"
        foot={
          <>
            <Button variant="ghost" onClick={() => setRemoving(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={busy !== ''}
              onClick={() =>
                void run('remove', async () => {
                  setView(await recordingsStorageApi.remove());
                  setForm(null);
                  setCheck(null);
                  setRemoving(false);
                })
              }
            >
              Remove it
            </Button>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>Stitch forgets the key and asks only the phone system for recordings again. Recordings it keeps in cloud storage can’t be played in Stitch then.</p>
      </Dialog>
    </Card>
  );
}
